import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    financeText,
    getFinanceLanguage,
    useFinanceDocumentLang,
    saveFinanceLanguage,
} from "finance-language";

import { Alert, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem, Snackbar, Stack, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AccountBalance,
    Add,
    ArrowDownward,
    ArrowForward,
    ArrowUpward,
    CreditCardOutlined,
    DeleteOutlineOutlined,
    EditOutlined,
    Euro,
    LocalAtmOutlined,
    Payments,
    PointOfSaleOutlined,
    ReceiptLongOutlined,
    Refresh,
    Search,
    SwapHorizOutlined,
    TrendingUp,
    Wallet,
    WarningAmberRounded,
} from "@mui/icons-material";

import {
    FinanceKpiTile,
    FinanceDetailsSection,
} from "../crm/invoice/finance/shared/FinanceUiKit";
// Koyu hero bandı için (yedekten geri yüklendi)
import { CheckCircle } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";
/* =========================================================
   CONFIG
========================================================= */

const API_URL =
    process.env.REACT_APP_API_URL;

/*
 * Senin mevcut server.js yapın:
 *
 * .use("/finance/cash", cashRoutes)
 *
 * Bu nedenle Company Cash endpointlerinin tamamı
 * /finance/cash altında çalışıyor.
 */
const CASH_API =
    "/finance/cash";

/* =========================================================
   CONSTANTS
========================================================= */

const CURRENCIES = [
    "TRY",
    "USD",
    "EUR",
    "GBP",
];

const PAYMENT_METHODS = [
    {
        value: "bank",
        label: "Bank",
    },
    {
        value: "cash",
        label: "Cash",
    },
    {
        value: "credit_card",
        label: "Credit Card",
    },
    {
        value: "pos",
        label: "POS",
    },
    {
        value: "cheque",
        label: "Cheque",
    },
    {
        value: "promissory_note",
        label: "Promissory Note",
    },
    {
        value: "virman",
        label: "Virman",
    },
    {
        value: "adjustment",
        label: "Adjustment",
    },
];

const emptyTransactionForm = {
    type: "cash_in",

    accountId: "",

    destinationAccountId: "",

    amount: "",

    category: "",

    referenceNo: "",

    description: "",
};

const emptyCashAccountForm = {
    name: "",

    currency: "TRY",

    openingBalance: "",

    description: "",
};

/* =========================================================
   COLORS
========================================================= */

const COLORS = {
    navy: "#0B1F3A",

    blue: "#175CD3",
    blueLight: "#EFF8FF",

    green: "#027A48",
    greenLight: "#ECFDF3",

    red: "#B42318",
    redLight: "#FEF3F2",

    amber: "#B54708",
    amberLight: "#FFFAEB",

    purple: "#6941C6",
    purpleLight: "#F4F3FF",

    text: "#101828",
    subtext: "#667085",

    border: "#E4E7EC",

    page: "#F5F7FA",

    white: "#FFFFFF",
};

const cardSx = {
    borderRadius: "10px",

    background: COLORS.white,

    border: "1px solid rgba(15, 23, 42, 0.12)",

    boxShadow: "none",

    overflow: "hidden",
};

/* =========================================================
   HELPERS
========================================================= */

const getId = (
    value
) => {
    if (!value) {
        return "";
    }

    if (
        typeof value ===
        "object"
    ) {
        return String(
            value._id ||
            value.id ||
            ""
        );
    }

    return String(
        value
    );
};

const normalizeText = (
    value
) =>
    String(
        value || ""
    ).trim();

/* =========================================================
   MONEY
========================================================= */

const formatMoney = (
    amount,
    currency = "EUR"
) => {
    try {
        return new Intl.NumberFormat(
            "tr-TR",
            {
                style:
                    "currency",

                currency,

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        ).format(
            Number(
                amount
            ) || 0
        );
    } catch {
        return `${
            Number(
                amount
            ) || 0
        } ${currency}`;
    }
};

/* =========================================================
   DATE
========================================================= */

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleString(
        "tr-TR"
    );
};

const isCurrentMonth = (
    value
) => {
    if (!value) {
        return false;
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return false;
    }

    const now =
        new Date();

    return (
        date.getFullYear() ===
        now.getFullYear() &&
        date.getMonth() ===
        now.getMonth()
    );
};

/* =========================================================
   NORMALIZE LIST
========================================================= */

const normalizeList = (
    payload
) => {
    let items = [];

    if (
        Array.isArray(
            payload
        )
    ) {
        items =
            payload;
    } else if (
        Array.isArray(
            payload?.data
        )
    ) {
        items =
            payload.data;
    } else if (
        Array.isArray(
            payload?.data?.data
        )
    ) {
        items =
            payload.data.data;
    } else if (
        Array.isArray(
            payload?.results
        )
    ) {
        items =
            payload.results;
    }

    return items.map(
        (
            item
        ) => {
            if (
                item?.attributes
            ) {
                return {
                    _id:
                        item.id ||
                        item.attributes?._id,

                    ...item.attributes,
                };
            }

            return item;
        }
    );
};

/* =========================================================
   NORMALIZE DASHBOARD
========================================================= */

const normalizeDashboard = (
    payload
) => {
    let source = {};

    if (
        payload?.summary
    ) {
        source =
            payload;
    } else if (
        payload?.data?.summary
    ) {
        source =
            payload.data;
    } else if (
        payload?.data?.data?.summary
    ) {
        source =
            payload.data.data;
    }

    return {
        summary: {
            transactionCount:
                Number(
                    source
                        ?.summary
                        ?.transactionCount
                ) || 0,

            accountCount:
                Number(
                    source
                        ?.summary
                        ?.accountCount
                ) || 0,
        },

        currencies: {
            TRY:
                Number(
                    source
                        ?.currencies
                        ?.TRY
                ) || 0,

            USD:
                Number(
                    source
                        ?.currencies
                        ?.USD
                ) || 0,

            EUR:
                Number(
                    source
                        ?.currencies
                        ?.EUR
                ) || 0,

            GBP:
                Number(
                    source
                        ?.currencies
                        ?.GBP
                ) || 0,
        },
    };
};

/* =========================================================
   PAYMENT METHOD
========================================================= */

const getPaymentMethodConfig = (
    method,
    account
) => {
    const resolvedMethod =
        method ||
        (
            account?.type ===
            "cash"
                ? "cash"
                : "bank"
        );

    const configs = {
        bank: {
            label:
                "Bank",

            color:
            COLORS.blue,

            background:
            COLORS.blueLight,

            border:
                "#B2DDFF",

            icon:
                <AccountBalance fontSize="small" />,
        },

        cash: {
            label:
                "Cash",

            color:
            COLORS.green,

            background:
            COLORS.greenLight,

            border:
                "#ABEFC6",

            icon:
                <Wallet fontSize="small" />,
        },

        credit_card: {
            label:
                "Credit Card",

            color:
            COLORS.purple,

            background:
            COLORS.purpleLight,

            border:
                "#D9D6FE",

            icon:
                <CreditCardOutlined fontSize="small" />,
        },

        pos: {
            label:
                "POS",

            color:
            COLORS.blue,

            background:
            COLORS.blueLight,

            border:
                "#B2DDFF",

            icon:
                <PointOfSaleOutlined fontSize="small" />,
        },

        cheque: {
            label:
                "Cheque",

            color:
            COLORS.amber,

            background:
            COLORS.amberLight,

            border:
                "#FEDF89",

            icon:
                <ReceiptLongOutlined fontSize="small" />,
        },

        promissory_note: {
            label:
                "Promissory Note",

            color:
            COLORS.amber,

            background:
            COLORS.amberLight,

            border:
                "#FEDF89",

            icon:
                <Payments fontSize="small" />,
        },

        virman: {
            label:
                "Virman",

            color:
            COLORS.blue,

            background:
            COLORS.blueLight,

            border:
                "#B2DDFF",

            icon:
                <SwapHorizOutlined fontSize="small" />,
        },

        adjustment: {
            label:
                "Adjustment",

            color:
            COLORS.amber,

            background:
            COLORS.amberLight,

            border:
                "#FEDF89",

            icon:
                <TrendingUp fontSize="small" />,
        },
    };

    return (
        configs[
            resolvedMethod
            ] || {
            label:
                resolvedMethod ||
                "-",

            color:
            COLORS.subtext,

            background:
                "#F2F4F7",

            border:
            COLORS.border,

            icon:
                <Payments fontSize="small" />,
        }
    );
};

/* =========================================================
   DIRECT EDITABLE TRANSACTION

   Company Cash ekranından yalnızca
   manuel Bank/Cash hareketleri edit/delete edilir.

   POS
   Credit Card
   Cheque
   Promissory Note
   {financeText("Virman")}

   kendi modüllerinden yönetilir.
========================================================= */

const isDirectEditableTransaction = (
    transaction
) => {
    if (
        !transaction
    ) {
        return false;
    }

    if (
        ![
            "cash_in",
            "cash_out",
        ].includes(
            transaction.type
        )
    ) {
        return false;
    }

    const paymentMethod =
        transaction.paymentMethod;

    if (
        paymentMethod &&
        ![
            "bank",
            "cash",
        ].includes(
            paymentMethod
        )
    ) {
        return false;
    }

    if (
        transaction.creditCardId ||
        transaction.posAccountId ||
        transaction.chequeId ||
        transaction.promissoryNoteId
    ) {
        return false;
    }

    return true;
};

/* =========================================================
   METRIC CARD
========================================================= */

// Görünüm Finance kitindeki KPI kartıyla aynı (bkz. shared/FinanceUiKit.js)
const MetricCard = (props) => <FinanceKpiTile {...props} />;

/* =========================================================
   ACCOUNT CARD
========================================================= */

const AccountCard = ({
                         account,
                     }) => {
    const isBank =
        account?.type ===
        "bank";

    const currency =
        account?.currency ||
        "EUR";

    const currentBalance =
        Number(
            account?.currentBalance
        ) || 0;

    const openingBalance =
        Number(
            account?.openingBalance
        ) || 0;

    const isActive =
        account?.isActive !==
        false;

    return (
        <Card
            sx={{
                borderRadius:
                    "10px",

                border:
                    `1px solid ${COLORS.border}`,

                boxShadow:
                    "0 5px 18px rgba(16,24,40,.045)",

                height:
                    "100%",

                background:
                    "#FFFFFF",

                transition:
                    "transform .2s ease, box-shadow .2s ease",

                "&:hover": {
                    transform:
                        "none",

                    boxShadow:
                        "0 12px 28px rgba(16,24,40,.09)",
                },
            }}
        >
            <MDBox
                p={2.25}
            >
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1.5}
                >
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.1}
                        minWidth={0}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            minWidth={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    isBank
                                        ? COLORS.blue
                                        : COLORS.green,

                                background:
                                    isBank
                                        ? COLORS.blueLight
                                        : COLORS.greenLight,

                                border:
                                    isBank
                                        ? "1px solid #B2DDFF"
                                        : "1px solid #ABEFC6",
                            }}
                        >
                            {isBank ? (
                                <AccountBalance />
                            ) : (
                                <LocalAtmOutlined />
                            )}
                        </MDBox>

                        <MDBox
                            minWidth={0}
                        >
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                display="block"
                                sx={{
                                    color:
                                    COLORS.text,

                                    lineHeight:
                                        1.3,
                                }}
                            >
                                {account?.name ||
                                    financeText("Cash Account")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{
                                    color:
                                    COLORS.subtext,

                                    mt:
                                        0.3,
                                }}
                            >
                                {financeText(
                                    isBank
                                        ? "Bank Account"
                                        : "Physical Cash"
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    <Chip
                        size="small"
                        label={
                            currency
                        }
                        sx={{
                            color:
                                isBank
                                    ? COLORS.blue
                                    : COLORS.green,

                            background:
                                isBank
                                    ? COLORS.blueLight
                                    : COLORS.greenLight,

                            border:
                                isBank
                                    ? "1px solid #B2DDFF"
                                    : "1px solid #ABEFC6",

                            fontWeight:
                                800,

                            flexShrink:
                                0,
                        }}
                    />
                </MDBox>

                <MDBox
                    mt={2.5}
                >
                    <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                            color:
                            COLORS.subtext,

                            letterSpacing:
                                ".06em",

                            textTransform:
                                "uppercase",
                        }}
                    >
                        {financeText("Current Balance")}
                    </MDTypography>

                    <MDTypography
                        variant="h4"
                        fontWeight="bold"
                        mt={0.5}
                        sx={{
                            color:
                                currentBalance >=
                                0
                                    ? COLORS.green
                                    : COLORS.red,
                        }}
                    >
                        {formatMoney(
                            currentBalance,
                            currency
                        )}
                    </MDTypography>
                </MDBox>

                <MDBox
                    mt={2}
                    pt={1.5}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    sx={{
                        borderTop:
                            `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{
                                color:
                                COLORS.subtext,
                            }}
                        >
                            {financeText("Opening Balance")}
                        </MDTypography>

                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                                color:
                                COLORS.text,
                            }}
                        >
                            {formatMoney(
                                openingBalance,
                                currency
                            )}
                        </MDTypography>
                    </MDBox>

                    <Chip
                        size="small"
                        label={financeText(
                            isActive
                                ? "Active"
                                : "Inactive"
                        )}
                        sx={{
                            color:
                                isActive
                                    ? COLORS.green
                                    : COLORS.red,

                            background:
                                isActive
                                    ? COLORS.greenLight
                                    : COLORS.redLight,

                            border:
                                isActive
                                    ? "1px solid #ABEFC6"
                                    : "1px solid #FECDCA",

                            fontWeight:
                                700,
                        }}
                    />
                </MDBox>
            </MDBox>
        </Card>
    );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const CompanyCash = () => {
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    useFinanceDocumentLang(financeLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const token =
        localStorage.getItem(
            "token"
        );

    /* =====================================================
       DATA
    ===================================================== */

    const [
        loading,
        setLoading,
    ] = useState(
        true
    );

    const [
        transactions,
        setTransactions,
    ] = useState(
        []
    );

    const [
        accounts,
        setAccounts,
    ] = useState(
        []
    );

    const [
        dashboard,
        setDashboard,
    ] = useState({
        summary: {
            transactionCount:
                0,

            accountCount:
                0,
        },

        currencies: {
            TRY: 0,
            USD: 0,
            EUR: 0,
            GBP: 0,
        },
    });

    const [
        lastUpdated,
        setLastUpdated,
    ] = useState(
        new Date()
    );

    /* =====================================================
       FILTERS
    ===================================================== */

    const [
        summaryCurrency,
        setSummaryCurrency,
    ] = useState(
        "EUR"
    );

    const [
        search,
        setSearch,
    ] = useState(
        ""
    );

    const [
        type,
        setType,
    ] = useState(
        "all"
    );

    const [
        currency,
        setCurrency,
    ] = useState(
        "all"
    );

    const [
        paymentMethodFilter,
        setPaymentMethodFilter,
    ] = useState(
        "all"
    );

    /* =====================================================
       TRANSACTION MODAL
    ===================================================== */

    const [
        transactionModal,
        setTransactionModal,
    ] = useState(
        false
    );

    const [
        editingTransaction,
        setEditingTransaction,
    ] = useState(
        null
    );

    const [
        deleteTransactionTarget,
        setDeleteTransactionTarget,
    ] = useState(
        null
    );

    const [
        transactionSaving,
        setTransactionSaving,
    ] = useState(
        false
    );

    const [
        transactionForm,
        setTransactionForm,
    ] = useState(
        emptyTransactionForm
    );

    /* =====================================================
       CASH ACCOUNT MODAL
    ===================================================== */

    const [
        cashAccountModal,
        setCashAccountModal,
    ] = useState(
        false
    );

    const [
        cashAccountSaving,
        setCashAccountSaving,
    ] = useState(
        false
    );

    const [
        cashAccountForm,
        setCashAccountForm,
    ] = useState(
        emptyCashAccountForm
    );

    /* =====================================================
       NOTIFICATION
    ===================================================== */

    const [
        notification,
        setNotification,
    ] = useState({
        open:
            false,

        message:
            "",

        severity:
            "success",
    });

    const showNotification =
        useCallback(
            (
                message,
                severity =
                    "success"
            ) => {
                setNotification({
                    open:
                        true,

                    message,

                    severity,
                });
            },

            []
        );

    const closeNotification =
        () => {
            setNotification(
                (
                    previous
                ) => ({
                    ...previous,

                    open:
                        false,
                })
            );
        };

    /* =====================================================
       API REQUEST
    ===================================================== */

    const apiRequest =
        useCallback(
            async (
                path,
                options = {}
            ) => {
                const baseUrl =
                    String(
                        API_URL ||
                        ""
                    ).replace(
                        /\/+$/,
                        ""
                    );

                const response =
                    await fetch(
                        `${baseUrl}${path}`,

                        {
                            ...options,

                            headers: {
                                "Content-Type":
                                    "application/json",

                                Authorization:
                                    `Bearer ${token}`,

                                ...(options.headers ||
                                    {}),
                            },
                        }
                    );

                let body =
                    null;

                try {
                    body =
                        await response.json();
                } catch {
                    body =
                        null;
                }

                if (
                    !response.ok
                ) {
                    throw new Error(
                        body
                            ?.errors?.[0]
                            ?.detail ||
                        body
                            ?.message ||
                        `Request failed with status ${response.status}`
                    );
                }

                return body;
            },

            [
                token,
            ]
        );

    /* =====================================================
       FETCH DATA

       SENİN BACKEND ROUTE YAPIN:

       /api/finance/cash/dashboard
       /api/finance/cash/accounts
       /api/finance/cash/transactions
    ===================================================== */

    const fetchData =
        useCallback(
            async () => {
                setLoading(
                    true
                );

                try {
                    const results =
                        await Promise.allSettled(
                            [
                                apiRequest(
                                    `${CASH_API}/dashboard`
                                ),

                                apiRequest(
                                    `${CASH_API}/accounts`
                                ),

                                apiRequest(
                                    `${CASH_API}/transactions`
                                ),
                            ]
                        );

                    const [
                        dashboardRequest,
                        accountsRequest,
                        transactionsRequest,
                    ] =
                        results;

                    let normalizedAccounts =
                        [];

                    let normalizedTransactions =
                        [];

                    /* =====================================
                       ACCOUNTS
                    ===================================== */

                    if (
                        accountsRequest.status ===
                        "fulfilled"
                    ) {
                        normalizedAccounts =
                            normalizeList(
                                accountsRequest.value
                            );

                        setAccounts(
                            normalizedAccounts
                        );
                    } else {
                        console.error(
                            "CASH ACCOUNTS REQUEST FAILED:",
                            accountsRequest.reason
                        );

                        setAccounts(
                            []
                        );
                    }

                    /* =====================================
                       TRANSACTIONS
                    ===================================== */

                    if (
                        transactionsRequest.status ===
                        "fulfilled"
                    ) {
                        normalizedTransactions =
                            normalizeList(
                                transactionsRequest.value
                            );

                        setTransactions(
                            normalizedTransactions
                        );
                    } else {
                        console.error(
                            "CASH TRANSACTIONS REQUEST FAILED:",
                            transactionsRequest.reason
                        );

                        setTransactions(
                            []
                        );
                    }

                    /* =====================================
                       DASHBOARD
                    ===================================== */

                    if (
                        dashboardRequest.status ===
                        "fulfilled"
                    ) {
                        setDashboard(
                            normalizeDashboard(
                                dashboardRequest.value
                            )
                        );
                    } else {
                        console.error(
                            "CASH DASHBOARD REQUEST FAILED:",
                            dashboardRequest.reason
                        );

                        /*
                         * Dashboard endpoint hata verirse
                         * account listesinden balance fallback.
                         */

                        const currencyBalances = {
                            TRY:
                                0,

                            USD:
                                0,

                            EUR:
                                0,

                            GBP:
                                0,
                        };

                        normalizedAccounts.forEach(
                            (
                                account
                            ) => {
                                const accountCurrency =
                                    account?.currency;

                                if (
                                    currencyBalances[
                                        accountCurrency
                                        ] !==
                                    undefined
                                ) {
                                    currencyBalances[
                                        accountCurrency
                                        ] +=
                                        Number(
                                            account.currentBalance
                                        ) ||
                                        0;
                                }
                            }
                        );

                        setDashboard({
                            summary: {
                                transactionCount:
                                normalizedTransactions.length,

                                accountCount:
                                normalizedAccounts.length,
                            },

                            currencies:
                            currencyBalances,
                        });
                    }

                    setLastUpdated(
                        new Date()
                    );
                } catch (
                    error
                    ) {
                    console.error(
                        "Unexpected Company Cash error:",
                        error
                    );

                    showNotification(
                        error?.message ||
                        financeText("Company Cash data could not be loaded."),

                        "error"
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },

            [
                apiRequest,
                showNotification,
            ]
        );

    useEffect(
        () => {
            fetchData();
        },

        [
            fetchData,
        ]
    );

    /* =====================================================
       ACTIVE ACCOUNTS
    ===================================================== */

    const activeAccounts =
        useMemo(
            () =>
                accounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false
                ),

            [
                accounts,
            ]
        );

    const bankAccountCount =
        useMemo(
            () =>
                activeAccounts.filter(
                    (
                        account
                    ) =>
                        account.type ===
                        "bank"
                ).length,

            [
                activeAccounts,
            ]
        );

    const physicalCashCount =
        useMemo(
            () =>
                activeAccounts.filter(
                    (
                        account
                    ) =>
                        account.type ===
                        "cash"
                ).length,

            [
                activeAccounts,
            ]
        );

    /* =====================================================
       MONTHLY CASH FLOW

       Virman:
       type = transfer

       Cash In / Cash Out hesaplarına girmez.
    ===================================================== */

    const transactionCurrencySummary =
        useMemo(
            () => {
                const result = {
                    TRY: {
                        cashIn:
                            0,

                        cashOut:
                            0,

                        net:
                            0,
                    },

                    USD: {
                        cashIn:
                            0,

                        cashOut:
                            0,

                        net:
                            0,
                    },

                    EUR: {
                        cashIn:
                            0,

                        cashOut:
                            0,

                        net:
                            0,
                    },

                    GBP: {
                        cashIn:
                            0,

                        cashOut:
                            0,

                        net:
                            0,
                    },
                };

                transactions.forEach(
                    (
                        transaction
                    ) => {
                        const transactionCurrency =
                            String(
                                transaction?.currency ||
                                ""
                            ).toUpperCase();

                        if (
                            !result[
                                transactionCurrency
                                ]
                        ) {
                            return;
                        }

                        const date =
                            transaction?.transactionDate ||
                            transaction?.createdAt;

                        if (
                            !isCurrentMonth(
                                date
                            )
                        ) {
                            return;
                        }

                        const amount =
                            Number(
                                transaction?.amount
                            ) ||
                            0;

                        if (
                            transaction.type ===
                            "cash_in"
                        ) {
                            result[
                                transactionCurrency
                                ].cashIn +=
                                amount;
                        }

                        if (
                            transaction.type ===
                            "cash_out"
                        ) {
                            result[
                                transactionCurrency
                                ].cashOut +=
                                amount;
                        }
                    }
                );

                CURRENCIES.forEach(
                    (
                        item
                    ) => {
                        result[
                            item
                            ].net =
                            result[
                                item
                                ].cashIn -
                            result[
                                item
                                ].cashOut;
                    }
                );

                return result;
            },

            [
                transactions,
            ]
        );

    const selectedSummary =
        transactionCurrencySummary[
            summaryCurrency
            ] || {
            cashIn:
                0,

            cashOut:
                0,

            net:
                0,
        };

    /* =====================================================
       FILTER TRANSACTIONS
    ===================================================== */

    const filteredTransactions =
        useMemo(
            () => {
                const query =
                    search
                        .trim()
                        .toLowerCase();

                return transactions.filter(
                    (
                        transaction
                    ) => {
                        const sourceAccount =
                            transaction?.accountId &&
                            typeof transaction.accountId ===
                            "object"
                                ? transaction.accountId
                                : transaction?.account;

                        const destinationAccount =
                            transaction?.destinationAccountId &&
                            typeof transaction.destinationAccountId ===
                            "object"
                                ? transaction.destinationAccountId
                                : null;

                        const resolvedPaymentMethod =
                            transaction.paymentMethod ||
                            (
                                sourceAccount?.type ===
                                "cash"
                                    ? "cash"
                                    : "bank"
                            );

                        const searchMatch =
                            !query ||
                            [
                                transaction.transactionNo,

                                transaction.referenceNo,

                                transaction.category,

                                transaction.description,

                                sourceAccount?.name,

                                destinationAccount?.name,

                                resolvedPaymentMethod,

                                transaction
                                    ?.creditCardId
                                    ?.name,

                                transaction
                                    ?.posAccountId
                                    ?.name,

                                transaction
                                    ?.chequeId
                                    ?.chequeNo,

                                transaction
                                    ?.promissoryNoteId
                                    ?.noteNo,
                            ].some(
                                (
                                    value
                                ) =>
                                    String(
                                        value ||
                                        ""
                                    )
                                        .toLowerCase()
                                        .includes(
                                            query
                                        )
                            );

                        const typeMatch =
                            type ===
                            "all" ||
                            transaction.type ===
                            type;

                        const currencyMatch =
                            currency ===
                            "all" ||
                            transaction.currency ===
                            currency;

                        const paymentMethodMatch =
                            paymentMethodFilter ===
                            "all" ||
                            resolvedPaymentMethod ===
                            paymentMethodFilter;

                        return (
                            searchMatch &&
                            typeMatch &&
                            currencyMatch &&
                            paymentMethodMatch
                        );
                    }
                );
            },

            [
                transactions,
                search,
                type,
                currency,
                paymentMethodFilter,
            ]
        );

    /* =====================================================
       TRANSACTION FORM RESET
    ===================================================== */

    const resetTransactionForm =
        () => {
            setTransactionForm(
                emptyTransactionForm
            );
        };

    /* =====================================================
       NEW TRANSACTION
    ===================================================== */

    const openNewTransaction =
        () => {
            setEditingTransaction(
                null
            );

            resetTransactionForm();

            setTransactionModal(
                true
            );
        };

    /* =====================================================
       EDIT TRANSACTION
    ===================================================== */

    const openEditTransaction =
        (
            transaction
        ) => {
            if (
                !isDirectEditableTransaction(
                    transaction
                )
            ) {
                showNotification(
                    financeText("This transaction is managed by another Treasury module and cannot be edited from Company Cash."),

                    "warning"
                );

                return;
            }

            setEditingTransaction(
                transaction
            );

            setTransactionForm({
                type:
                    transaction.type ||
                    "cash_in",

                accountId:
                    getId(
                        transaction.accountId
                    ),

                destinationAccountId:
                    "",

                amount:
                    transaction.amount ??
                    "",

                category:
                    transaction.category ||
                    "",

                referenceNo:
                    transaction.referenceNo ||
                    "",

                description:
                    transaction.description ||
                    "",
            });

            setTransactionModal(
                true
            );
        };

    /* =====================================================
       CLOSE TRANSACTION
    ===================================================== */

    const closeTransactionModal =
        () => {
            if (
                transactionSaving
            ) {
                return;
            }

            setTransactionModal(
                false
            );

            setEditingTransaction(
                null
            );

            resetTransactionForm();
        };

    /* =====================================================
       SELECTED SOURCE ACCOUNT
    ===================================================== */

    const selectedTransactionAccount =
        useMemo(
            () =>
                accounts.find(
                    (
                        account
                    ) =>
                        getId(
                            account
                        ) ===
                        String(
                            transactionForm.accountId
                        )
                ) ||
                null,

            [
                accounts,
                transactionForm.accountId,
            ]
        );

    /*
     * Düzenleme sırasında mevcut bakiye, düzenlenen hareketin
     * kendi etkisini içerir. Aynı hesapta bir cash_out
     * düzenleniyorsa eski tutar önce geri verilmiş sayılır
     * (backend de aynı delta mantığını uygular).
     */
    const transactionAvailableBalance =
        useMemo(
            () => {
                if (
                    !selectedTransactionAccount
                ) {
                    return 0;
                }

                const balance =
                    Number(
                        selectedTransactionAccount.currentBalance
                    ) || 0;

                if (
                    editingTransaction &&
                    editingTransaction.type ===
                    "cash_out" &&
                    getId(
                        editingTransaction.accountId
                    ) ===
                    getId(
                        selectedTransactionAccount
                    )
                ) {
                    return (
                        balance +
                        (Number(
                            editingTransaction.amount
                        ) || 0)
                    );
                }

                if (
                    editingTransaction &&
                    editingTransaction.type ===
                    "cash_in" &&
                    getId(
                        editingTransaction.accountId
                    ) ===
                    getId(
                        selectedTransactionAccount
                    )
                ) {
                    return (
                        balance -
                        (Number(
                            editingTransaction.amount
                        ) || 0)
                    );
                }

                return balance;
            },

            [
                selectedTransactionAccount,
                editingTransaction,
            ]
        );

    /* =====================================================
       VIRMAN DESTINATION ACCOUNTS

       - active
       - different account
       - same currency
    ===================================================== */

    const virmanDestinationAccounts =
        useMemo(
            () => {
                if (
                    !selectedTransactionAccount
                ) {
                    return [];
                }

                return accounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false &&

                        getId(
                            account
                        ) !==
                        getId(
                            selectedTransactionAccount
                        ) &&

                        account.currency ===
                        selectedTransactionAccount.currency
                );
            },

            [
                accounts,
                selectedTransactionAccount,
            ]
        );

    const selectedDestinationAccount =
        useMemo(
            () =>
                virmanDestinationAccounts.find(
                    (
                        account
                    ) =>
                        getId(
                            account
                        ) ===
                        String(
                            transactionForm.destinationAccountId
                        )
                ) ||
                null,

            [
                virmanDestinationAccounts,
                transactionForm.destinationAccountId,
            ]
        );

    /* =====================================================
       DIRECT PAYMENT METHOD
    ===================================================== */

    const directPaymentMethod =
        selectedTransactionAccount?.type ===
        "cash"
            ? "cash"
            : "bank";

    /* =====================================================
       TRANSACTION TYPE CHANGE
    ===================================================== */

    const handleTransactionTypeChange =
        (
            value
        ) => {
            setTransactionForm(
                (
                    previous
                ) => ({
                    ...previous,

                    type:
                    value,

                    destinationAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       SOURCE ACCOUNT CHANGE
    ===================================================== */

    const handleSourceAccountChange =
        (
            accountId
        ) => {
            setTransactionForm(
                (
                    previous
                ) => ({
                    ...previous,

                    accountId,

                    destinationAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       SAVE TRANSACTION
    ===================================================== */

    const saveTransaction =
        async () => {
            try {
                /* =========================================
                   SOURCE ACCOUNT
                ========================================= */

                if (
                    !transactionForm.accountId
                ) {
                    showNotification(
                        transactionForm.type ===
                        "virman"
                            ? financeText("Please select a source account.")
                            : financeText("Please select an account."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   AMOUNT
                ========================================= */

                const amount =
                    Number(
                        transactionForm.amount
                    );

                if (
                    !Number.isFinite(
                        amount
                    ) ||
                    amount <=
                    0
                ) {
                    showNotification(
                        financeText("Amount must be greater than zero."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   VIRMAN
                ========================================= */

                if (
                    !editingTransaction &&
                    transactionForm.type ===
                    "virman"
                ) {
                    if (
                        !transactionForm.destinationAccountId
                    ) {
                        showNotification(
                            financeText("Please select a destination account."),

                            "warning"
                        );

                        return;
                    }

                    if (
                        !selectedTransactionAccount ||
                        !selectedDestinationAccount
                    ) {
                        showNotification(
                            financeText("Source or destination account could not be found."),

                            "warning"
                        );

                        return;
                    }

                    if (
                        selectedTransactionAccount.currency !==
                        selectedDestinationAccount.currency
                    ) {
                        showNotification(
                            financeText("Virman can only be made between accounts with the same currency."),

                            "warning"
                        );

                        return;
                    }

                    if (
                        amount >
                        Number(
                            selectedTransactionAccount.currentBalance
                        )
                    ) {
                        showNotification(
                            financeText("Source account has insufficient balance."),
                            "warning"
                        );

                        return;
                    }

                    setTransactionSaving(
                        true
                    );

                    /*
                     * POST
                     * /api/finance/cash/virman
                     */

                    await apiRequest(
                        `${CASH_API}/virman`,

                        {
                            method:
                                "POST",

                            body:
                                JSON.stringify({
                                    sourceAccountId:
                                    transactionForm.accountId,

                                    destinationAccountId:
                                    transactionForm.destinationAccountId,

                                    amount,

                                    referenceNo:
                                        normalizeText(
                                            transactionForm.referenceNo
                                        ),

                                    description:
                                        normalizeText(
                                            transactionForm.description
                                        ),
                                }),
                        }
                    );

                    setTransactionModal(
                        false
                    );

                    setEditingTransaction(
                        null
                    );

                    resetTransactionForm();

                    await fetchData();

                    showNotification(
                        financeText("Virman completed successfully.")
                    );

                    return;
                }

                /* =========================================
                   CASH OUT VALIDATION
                ========================================= */

                if (
                    transactionForm.type ===
                    "cash_out" &&
                    selectedTransactionAccount &&
                    amount >
                    transactionAvailableBalance
                ) {
                    showNotification(
                        financeText("Selected account has insufficient balance."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   DIRECT TRANSACTION PAYLOAD
                ========================================= */

                setTransactionSaving(
                    true
                );

                const payload = {
                    type:
                    transactionForm.type,

                    accountId:
                    transactionForm.accountId,

                    amount,

                    paymentMethod:
                    directPaymentMethod,

                    category:
                        normalizeText(
                            transactionForm.category
                        ),

                    referenceNo:
                        normalizeText(
                            transactionForm.referenceNo
                        ),

                    description:
                        normalizeText(
                            transactionForm.description
                        ),
                };

                /* =========================================
                   EDIT

                   PATCH
                   /api/finance/cash/transactions/:id
                ========================================= */

                if (
                    editingTransaction
                ) {
                    const transactionId =
                        getId(
                            editingTransaction
                        );

                    await apiRequest(
                        `${CASH_API}/transactions/${transactionId}`,

                        {
                            method:
                                "PATCH",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Transaction updated successfully.")
                    );
                } else {
                    /* =====================================
                       CREATE

                       POST
                       /api/finance/cash/transactions
                    ===================================== */

                    await apiRequest(
                        `${CASH_API}/transactions`,

                        {
                            method:
                                "POST",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("Transaction created successfully.")
                    );
                }

                setTransactionModal(
                    false
                );

                setEditingTransaction(
                    null
                );

                resetTransactionForm();

                await fetchData();
            } catch (
                error
                ) {
                console.error(
                    "saveTransaction error:",
                    error
                );

                showNotification(
                    error?.message ||
                    financeText("Transaction could not be saved."),

                    "error"
                );
            } finally {
                setTransactionSaving(
                    false
                );
            }
        };

    /* =====================================================
       DELETE TRANSACTION
    ===================================================== */

    const confirmDeleteTransaction =
        async () => {
            if (
                !deleteTransactionTarget
            ) {
                return;
            }

            try {
                setTransactionSaving(
                    true
                );

                const transactionId =
                    getId(
                        deleteTransactionTarget
                    );

                /*
                 * DELETE
                 * /api/finance/cash/transactions/:id
                 */

                await apiRequest(
                    `${CASH_API}/transactions/${transactionId}`,

                    {
                        method:
                            "DELETE",
                    }
                );

                setDeleteTransactionTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("Transaction deleted successfully.")
                );
            } catch (
                error
                ) {
                console.error(
                    "delete transaction error:",
                    error
                );

                showNotification(
                    error?.message ||
                    financeText("Transaction could not be deleted."),

                    "error"
                );
            } finally {
                setTransactionSaving(
                    false
                );
            }
        };

    /* =====================================================
       CASH ACCOUNT MODAL
    ===================================================== */

    const openCashAccountModal =
        () => {
            setCashAccountForm(
                emptyCashAccountForm
            );

            setCashAccountModal(
                true
            );
        };

    const closeCashAccountModal =
        () => {
            if (
                cashAccountSaving
            ) {
                return;
            }

            setCashAccountModal(
                false
            );

            setCashAccountForm(
                emptyCashAccountForm
            );
        };

    /* =====================================================
       SAVE PHYSICAL CASH ACCOUNT
    ===================================================== */

    const saveCashAccount =
        async () => {
            try {
                if (
                    !normalizeText(
                        cashAccountForm.name
                    )
                ) {
                    showNotification(
                        financeText("Cash account name is required."),

                        "warning"
                    );

                    return;
                }

                const openingBalance =
                    Number(
                        cashAccountForm.openingBalance ||
                        0
                    );

                if (
                    !Number.isFinite(
                        openingBalance
                    )
                ) {
                    showNotification(
                        financeText("Opening balance is invalid."),

                        "warning"
                    );

                    return;
                }

                setCashAccountSaving(
                    true
                );

                /*
                 * POST
                 * /api/finance/cash/accounts
                 */

                await apiRequest(
                    `${CASH_API}/accounts`,

                    {
                        method:
                            "POST",

                        body:
                            JSON.stringify({
                                name:
                                    normalizeText(
                                        cashAccountForm.name
                                    ),

                                type:
                                    "cash",

                                currency:
                                cashAccountForm.currency,

                                openingBalance,

                                description:
                                    normalizeText(
                                        cashAccountForm.description
                                    ),
                            }),
                    }
                );

                setCashAccountModal(
                    false
                );

                setCashAccountForm(
                    emptyCashAccountForm
                );

                await fetchData();

                showNotification(
                    financeText("Physical cash account created successfully.")
                );
            } catch (
                error
                ) {
                console.error(
                    "saveCashAccount error:",
                    error
                );

                showNotification(
                    error?.message ||
                    financeText("Cash account could not be created."),

                    "error"
                );
            } finally {
                setCashAccountSaving(
                    false
                );
            }
        };

    /* =====================================================
       TABLE
    ===================================================== */

    const tableData =
        useMemo(
            () => ({
                columns: [
                    /* =====================================
                       TRANSACTION
                    ===================================== */

                    {
                        Header:
                            financeText("TRANSACTION"),

                        accessor:
                            "transactionNo",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const incoming =
                                item.type ===
                                "cash_in";

                            const transfer =
                                item.type ===
                                "transfer";

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <MDBox
                                        width={38}
                                        height={38}
                                        minWidth={38}
                                        borderRadius="11px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color:
                                                transfer
                                                    ? COLORS.blue
                                                    : incoming
                                                        ? COLORS.green
                                                        : COLORS.red,

                                            background:
                                                transfer
                                                    ? COLORS.blueLight
                                                    : incoming
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                        }}
                                    >
                                        {transfer ? (
                                            <SwapHorizOutlined />
                                        ) : incoming ? (
                                            <ArrowDownward />
                                        ) : (
                                            <ArrowUpward />
                                        )}
                                    </MDBox>

                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {item.transactionNo ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {formatDate(
                                                item.transactionDate ||
                                                item.createdAt
                                            )}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       TYPE
                    ===================================== */

                    {
                        Header:
                            financeText("TYPE"),

                        accessor:
                            "type",

                        Cell: ({
                                   value,
                               }) => {
                            const configs = {
                                cash_in: {
                                    label:
                                        "Cash In",

                                    color:
                                    COLORS.green,

                                    background:
                                    COLORS.greenLight,
                                },

                                cash_out: {
                                    label:
                                        "Cash Out",

                                    color:
                                    COLORS.red,

                                    background:
                                    COLORS.redLight,
                                },

                                transfer: {
                                    label:
                                        "Virman",

                                    color:
                                    COLORS.blue,

                                    background:
                                    COLORS.blueLight,
                                },

                                adjustment: {
                                    label:
                                        "Adjustment",

                                    color:
                                    COLORS.amber,

                                    background:
                                    COLORS.amberLight,
                                },
                            };

                            const selected =
                                configs[value] || {
                                    label:
                                        value ||
                                        "-",

                                    color:
                                    COLORS.subtext,

                                    background:
                                        "#F2F4F7",
                                };

                            return (
                                <Chip
                                    size="small"
                                    label={
                                        financeText(
                                            selected.label
                                        )
                                    }
                                    sx={{
                                        color:
                                        selected.color,

                                        background:
                                        selected.background,

                                        fontWeight:
                                            700,
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       PAYMENT METHOD
                    ===================================== */

                    {
                        Header:
                            financeText("PAYMENT METHOD"),

                        id:
                            "paymentMethod",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const sourceAccount =
                                item?.accountId &&
                                typeof item.accountId ===
                                "object"
                                    ? item.accountId
                                    : item?.account;

                            const config =
                                getPaymentMethodConfig(
                                    item.paymentMethod,
                                    sourceAccount
                                );

                            return (
                                <Chip
                                    size="small"
                                    icon={
                                        config.icon
                                    }
                                    label={
                                        financeText(
                                            config.label
                                        )
                                    }
                                    sx={{
                                        color:
                                        config.color,

                                        background:
                                        config.background,

                                        border:
                                            `1px solid ${config.border}`,

                                        fontWeight:
                                            700,

                                        "& .MuiChip-icon":
                                            {
                                                color:
                                                    `${config.color} !important`,
                                            },
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       FROM / ACCOUNT
                    ===================================== */

                    {
                        Header:
                            financeText("FROM / ACCOUNT"),

                        id:
                            "sourceAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const account =
                                item?.accountId &&
                                typeof item.accountId ===
                                "object"
                                    ? item.accountId
                                    : item?.account;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {account?.name ||
                                            "-"}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText(
                                            account?.type ===
                                            "bank"
                                                ? "Bank Account"
                                                : account?.type ===
                                                "cash"
                                                    ? "Physical Cash"
                                                    : "Account"
                                        )}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       TO
                    ===================================== */

                    {
                        Header:
                            financeText("TO"),

                        id:
                            "destinationAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const account =
                                item?.destinationAccountId &&
                                typeof item.destinationAccountId ===
                                "object"
                                    ? item.destinationAccountId
                                    : null;

                            if (
                                item.type !==
                                "transfer"
                            ) {
                                return (
                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        -
                                    </MDTypography>
                                );
                            }

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={0.7}
                                >
                                    <ArrowForward
                                        fontSize="small"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    />

                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {account?.name ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                account?.type ===
                                                "cash"
                                                    ? "Physical Cash"
                                                    : "Bank Account"
                                            )}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       CURRENCY
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENCY"),

                        accessor:
                            "currency",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={
                                    value ||
                                    "-"
                                }
                                sx={{
                                    color:
                                    COLORS.blue,

                                    background:
                                    COLORS.blueLight,

                                    border:
                                        "1px solid #B2DDFF",

                                    fontWeight:
                                        800,

                                    minWidth:
                                        56,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       CATEGORY
                    ===================================== */

                    {
                        Header:
                            financeText("CATEGORY"),

                        accessor:
                            "category",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="button"
                                sx={{
                                    color:
                                    COLORS.text,

                                    whiteSpace:
                                        "nowrap",
                                }}
                            >
                                {value
                                    ? financeText(value)
                                    : "-"}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       AMOUNT
                    ===================================== */

                    {
                        Header:
                            financeText("AMOUNT"),

                        accessor:
                            "amount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const isOut =
                                item.type ===
                                "cash_out";

                            const isTransfer =
                                item.type ===
                                "transfer";

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            isTransfer
                                                ? COLORS.blue
                                                : isOut
                                                    ? COLORS.red
                                                    : COLORS.green,

                                        whiteSpace:
                                            "nowrap",
                                    }}
                                >
                                    {isOut
                                        ? "-"
                                        : isTransfer
                                            ? ""
                                            : "+"}

                                    {formatMoney(
                                        item.amount,

                                        item.currency ||
                                        "EUR"
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       SOURCE
                    ===================================== */

                    {
                        Header:
                            financeText("SOURCE"),

                        id:
                            "sourceDetail",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            if (
                                item.creditCardId
                            ) {
                                return (
                                    <MDBox>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.purple,
                                            }}
                                        >
                                            {item.creditCardId.name ||
                                                financeText("Credit Card")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            ****
                                            {item.creditCardId.lastFourDigits ||
                                                ""}
                                        </MDTypography>
                                    </MDBox>
                                );
                            }

                            if (
                                item.posAccountId
                            ) {
                                return (
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    >
                                        {item.posAccountId.name ||
                                            financeText("POS")}
                                    </MDTypography>
                                );
                            }

                            if (
                                item.chequeId
                            ) {
                                return (
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.amber,
                                        }}
                                    >
                                        {financeText("Cheque")} #
                                        {item.chequeId.chequeNo ||
                                            "-"}
                                    </MDTypography>
                                );
                            }

                            if (
                                item.promissoryNoteId
                            ) {
                                return (
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.amber,
                                        }}
                                    >
                                        {financeText("Note")} #
                                        {item.promissoryNoteId.noteNo ||
                                            "-"}
                                    </MDTypography>
                                );
                            }

                            if (
                                item.type ===
                                "transfer"
                            ) {
                                return (
                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    >
                                        {financeText("Internal Transfer")}
                                    </MDTypography>
                                );
                            }

                            return (
                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText("Manual")}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       REFERENCE
                    ===================================== */

                    {
                        Header:
                            financeText("REFERENCE"),

                        accessor:
                            "referenceNo",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {value ||
                                    "-"}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       DESCRIPTION
                    ===================================== */

                    {
                        Header:
                            financeText("DESCRIPTION"),

                        accessor:
                            "description",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,

                                    display:
                                        "block",

                                    maxWidth:
                                        220,

                                    overflow:
                                        "hidden",

                                    textOverflow:
                                        "ellipsis",

                                    whiteSpace:
                                        "nowrap",
                                }}
                            >
                                {value ||
                                    "-"}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       ACTIONS
                    ===================================== */

                    {
                        Header:
                            financeText("ACTIONS"),

                        id:
                            "actions",

                        disableSortBy:
                            true,

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const editable =
                                isDirectEditableTransaction(
                                    item
                                );

                            return (
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={0.6}
                                >
                                    <Tooltip
                                        title={
                                            editable
                                                ? financeText("Edit transaction")
                                                : financeText("This transaction is managed by its Treasury source module")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !editable
                                                }
                                                onClick={() =>
                                                    openEditTransaction(
                                                        item
                                                    )
                                                }
                                                sx={{
                                                    width:
                                                        35,

                                                    height:
                                                        35,

                                                    color:
                                                    COLORS.purple,

                                                    background:
                                                    COLORS.purpleLight,

                                                    border:
                                                        "1px solid #D9D6FE",

                                                    borderRadius:
                                                        "9px",

                                                    "&:hover":
                                                        {
                                                            background:
                                                                "#E9E7FD",
                                                        },

                                                    "&.Mui-disabled":
                                                        {
                                                            background:
                                                                "#F2F4F7",

                                                            color:
                                                                "#98A2B3",

                                                            border:
                                                                `1px solid ${COLORS.border}`,
                                                        },
                                                }}
                                            >
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip
                                        title={
                                            editable
                                                ? financeText("Delete transaction")
                                                : financeText("This transaction is managed by its Treasury source module")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !editable
                                                }
                                                onClick={() =>
                                                    setDeleteTransactionTarget(
                                                        item
                                                    )
                                                }
                                                sx={{
                                                    width:
                                                        35,

                                                    height:
                                                        35,

                                                    color:
                                                    COLORS.red,

                                                    background:
                                                    COLORS.redLight,

                                                    border:
                                                        "1px solid #FECDCA",

                                                    borderRadius:
                                                        "9px",

                                                    "&:hover":
                                                        {
                                                            background:
                                                                "#FEE4E2",
                                                        },

                                                    "&.Mui-disabled":
                                                        {
                                                            background:
                                                                "#F2F4F7",

                                                            color:
                                                                "#98A2B3",

                                                            border:
                                                                `1px solid ${COLORS.border}`,
                                                        },
                                                }}
                                            >
                                                <DeleteOutlineOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </MDBox>
                            );
                        },
                    },
                ],

                rows:
                filteredTransactions,
            }),

            [
                filteredTransactions,
                financeLanguage,
            ]
        );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs:
                            -2,

                        md:
                            -3,
                    },

                    px: {
                        xs:
                            2,

                        md:
                            3,
                    },
                }}
            >
                {/* =================================================
                    HERO
                ================================================= */}

                <Card
                    sx={{
                        borderRadius:
                            "22px",

                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow:
                            "0 18px 46px rgba(11,31,58,.22)",

                        overflow:
                            "hidden",

                        position:
                            "relative",

                        mb:
                            3,

                        "&::after": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                360,

                            height:
                                360,

                            borderRadius:
                                "50%",

                            right:
                                -130,

                            top:
                                -180,

                            background:
                                "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                230,

                            height:
                                230,

                            borderRadius:
                                "50%",

                            right:
                                120,

                            bottom:
                                -170,

                            background:
                                "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <Wallet
                                            sx={{
                                                color:
                                                    "#FFFFFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE TREASURY")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "#FFFFFF",

                                        lineHeight:
                                            1.12,
                                    }}
                                >
                                    {financeText("Company Cash")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        maxWidth:
                                            760,
                                    }}
                                >
                                    {financeText(
                                        "Manage company bank accounts, physical cash, collections, payments, Treasury settlements and internal Virman transfers from one workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    gap={1}
                                    flexWrap="wrap"
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${transactions.length} ${financeText("transactions")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <AccountBalance
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${bankAccountCount} ${financeText("bank accounts")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <LocalAtmOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${physicalCashCount} ${financeText("cash registers")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color:
                                                        "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString(
                                            [],
                                            {
                                                hour:
                                                    "2-digit",

                                                minute:
                                                    "2-digit",
                                            }
                                        )}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",

                                        border:
                                            "1px solid rgba(255,255,255,.13)",

                                        backdropFilter:
                                            "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <MDBox>
                                            <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                        "rgba(255,255,255,.72)",
                                                }}
                                            >
                                                {financeText("CURRENT CASH POSITION")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    color:
                                                        "#B2DDFF",

                                                    fontWeight:
                                                        700,

                                                    mt:
                                                        0.4,
                                                }}
                                            >
                                                {
                                                    summaryCurrency
                                                }
                                            </MDTypography>
                                        </MDBox>

                                        <Wallet
                                            sx={{
                                                color:
                                                    "#B2DDFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        mt={0.7}
                                        sx={{
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        {formatMoney(
                                            dashboard
                                                .currencies[
                                                summaryCurrency
                                                ] ||
                                            0,

                                            summaryCurrency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.5}
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("Cash In")}{" "}
                                        <strong>
                                            {formatMoney(
                                                selectedSummary.cashIn,

                                                summaryCurrency
                                            )}
                                        </strong>

                                        {" · "}

                                        {financeText("Cash Out")}{" "}
                                        <strong>
                                            {formatMoney(
                                                selectedSummary.cashOut,

                                                summaryCurrency
                                            )}
                                        </strong>
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.4}
                                        sx={{
                                            color:
                                                selectedSummary.net >=
                                                0
                                                    ? "#75E0A7"
                                                    : "#FDA29B",

                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        {financeText("Net Cash Flow")}{" "}
                                        {formatMoney(
                                            selectedSummary.net,

                                            summaryCurrency
                                        )}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        mt={2}
                                        alignItems="stretch"
                                    >
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                openNewTransaction
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color:
                                                    `${COLORS.navy} !important`,

                                                flex:
                                                    1,

                                                boxShadow:
                                                    "none",
                                            }}
                                        >
                                            <Add
                                                sx={{
                                                    mr:
                                                        0.5,
                                                }}
                                            />

                                            {financeText("New Transaction")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh")}>
                                            <span style={{ display: "inline-flex" }}><IconButton
                                                onClick={
                                                    fetchData
                                                }
                                                disabled={
                                                    loading
                                                }
                                                sx={{
                                                    color:
                                                        "#FFFFFF",

                                                    border:
                                                        "1px solid rgba(255,255,255,.18)",

                                                    borderRadius:
                                                        "10px",
                                                }}
                                            >
                                                {loading ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            color:
                                                                "#FFFFFF",
                                                        }}
                                                    />
                                                ) : (
                                                    <Refresh />
                                                )}
                                            </IconButton></span>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* =================================================
                    SUMMARY CURRENCY
                ================================================= */}

                <MDBox
                    display="flex"
                    justifyContent={{
                        xs:
                            "stretch",

                        sm:
                            "flex-end",
                    }}
                    mb={1.5}
                >
                    <TextField
                        select
                        size="small"
                        label={financeText("Summary Currency")}
                        value={
                            summaryCurrency
                        }
                        onChange={(
                            event
                        ) =>
                            setSummaryCurrency(
                                event.target.value
                            )
                        }
                        sx={{
                            width: {
                                xs:
                                    "100%",

                                sm:
                                    210,
                            },

                            "& .MuiOutlinedInput-root":
                                {
                                    borderRadius:
                                        "11px",

                                    background:
                                        "#FFFFFF",
                                },
                        }}
                    >
                        <MenuItem value="TRY">
                            TRY - {financeText("Turkish Lira")}
                        </MenuItem>

                        <MenuItem value="USD">
                            USD - {financeText("US Dollar")}
                        </MenuItem>

                        <MenuItem value="EUR">
                            EUR - {financeText("Euro")}
                        </MenuItem>

                        <MenuItem value="GBP">
                            GBP - {financeText("British Pound")}
                        </MenuItem>
                    </TextField>
                </MDBox>

                {/* =================================================
                    MAIN METRICS
                ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Accounts")}
                            value={
                                activeAccounts.length
                            }
                            subtitle={`${bankAccountCount} ${financeText("bank")} · ${physicalCashCount} ${financeText("physical cash")}`}
                            icon={
                                <AccountBalance />
                            }
                            tone="navy"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={`${summaryCurrency} ${financeText("Cash In")}`}
                            value={formatMoney(
                                selectedSummary.cashIn,

                                summaryCurrency
                            )}
                            subtitle={`${summaryCurrency} ${financeText("inflow")} · ${financeText("Current month")}`}
                            icon={
                                <ArrowDownward />
                            }
                            tone="green"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={`${summaryCurrency} ${financeText("Cash Out")}`}
                            value={formatMoney(
                                selectedSummary.cashOut,

                                summaryCurrency
                            )}
                            subtitle={`${summaryCurrency} ${financeText("outflow")} · ${financeText("Current month")}`}
                            icon={
                                <ArrowUpward />
                            }
                            tone="red"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={`${summaryCurrency} ${financeText("Net Cash Flow")}`}
                            value={formatMoney(
                                selectedSummary.net,

                                summaryCurrency
                            )}
                            subtitle={financeText("Virman is excluded from cash flow")}
                            icon={
                                <TrendingUp />
                            }
                            tone={
                                selectedSummary.net >=
                                0
                                    ? "blue"
                                    : "red"
                            }
                        />
                    </Grid>
                </Grid>

                {/* =================================================
                    CURRENCY BALANCES
                ================================================= */}


                {/* =================================================
                    ACCOUNTS
                ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mt:
                            2.5,
                    }}
                >
                    <MDBox
                        p={3}
                    >
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1.5}
                            mb={2.5}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Bank & Cash Accounts")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Bank accounts are connected through Bank Accounts. Physical cash registers can be created here."
                                    )}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                display="flex"
                                gap={1}
                                flexWrap="wrap"
                            >
                                <Chip
                                    size="small"
                                    label={`${bankAccountCount} ${financeText("Bank")}`}
                                    sx={{
                                        color:
                                        COLORS.blue,

                                        background:
                                        COLORS.blueLight,

                                        fontWeight:
                                            700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={`${physicalCashCount} ${financeText("Cash")}`}
                                    sx={{
                                        color:
                                        COLORS.green,

                                        background:
                                        COLORS.greenLight,

                                        fontWeight:
                                            700,
                                    }}
                                />

                                <MDButton
                                    variant="contained"
                                    onClick={
                                        openCashAccountModal
                                    }
                                    sx={{
                                        background:
                                            `${COLORS.green} !important`,

                                        color:
                                            "#FFFFFF !important",

                                        borderRadius:
                                            "10px",

                                        boxShadow:
                                            "none",
                                    }}
                                >
                                    <Add
                                        sx={{
                                            mr:
                                                0.5,
                                        }}
                                    />

                                    {financeText("Add Cash Register")}
                                </MDButton>
                            </MDBox>
                        </MDBox>

                        {loading ? (
                            <MDBox
                                py={5}
                                display="flex"
                                justifyContent="center"
                            >
                                <CircularProgress />
                            </MDBox>
                        ) : accounts.length ===
                        0 ? (
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText("No Company Cash accounts have been created yet.")}
                            </Alert>
                        ) : (
                            <Grid
                                container
                                spacing={2}
                            >
                                {accounts.map(
                                    (
                                        account
                                    ) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            lg={4}
                                            xl={3}
                                            key={
                                                getId(
                                                    account
                                                )
                                            }
                                        >
                                            <AccountCard
                                                account={
                                                    account
                                                }
                                            />
                                        </Grid>
                                    )
                                )}
                            </Grid>
                        )}
                    </MDBox>
                </Card>

                {/* =================================================
                    TRANSACTIONS
                ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mt:
                            2.5,
                    }}
                >
                    <MDBox
                        p={3}
                    >
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1.5}
                            mb={2.5}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Treasury Transactions")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Manual cash movements, Virman, credit card, POS, cheque and promissory note settlements are displayed together."
                                    )}
                                </MDTypography>
                            </MDBox>

                            <MDButton
                                variant="contained"
                                onClick={
                                    openNewTransaction
                                }
                                sx={{
                                    background:
                                        `${COLORS.navy} !important`,

                                    color:
                                        "#FFFFFF !important",

                                    borderRadius:
                                        "10px",

                                    boxShadow:
                                        "none",
                                }}
                            >
                                <Add
                                    sx={{
                                        mr:
                                            0.5,
                                    }}
                                />

                                {financeText("New Transaction")}
                            </MDButton>
                        </MDBox>

                        {/* FILTERS */}

                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Search")}
                                    placeholder={financeText("Transaction, account, source, reference...")}
                                    value={
                                        search
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setSearch(
                                            event.target.value
                                        )
                                    }
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2.5}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Type")}
                                    value={
                                        type
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setType(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Types")}
                                    </MenuItem>

                                    <MenuItem value="cash_in">
                                        {financeText("Cash In")}
                                    </MenuItem>

                                    <MenuItem value="cash_out">
                                        {financeText("Cash Out")}
                                    </MenuItem>

                                    <MenuItem value="transfer">
                                        {financeText("Virman")}
                                    </MenuItem>

                                    <MenuItem value="adjustment">
                                        {financeText("Adjustment")}
                                    </MenuItem>
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={
                                        currency
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setCurrency(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Currencies")}
                                    </MenuItem>

                                    {CURRENCIES.map(
                                        (
                                            item
                                        ) => (
                                            <MenuItem
                                                key={
                                                    item
                                                }
                                                value={
                                                    item
                                                }
                                            >
                                                {item}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                lg={2.5}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Payment Method")}
                                    value={
                                        paymentMethodFilter
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setPaymentMethodFilter(
                                            event.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="all">
                                        {financeText("All Methods")}
                                    </MenuItem>

                                    {PAYMENT_METHODS.map(
                                        (
                                            item
                                        ) => (
                                            <MenuItem
                                                key={
                                                    item.value
                                                }
                                                value={
                                                    item.value
                                                }
                                            >
                                                {financeText(item.label)}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* INFO */}

                        <MDBox
                            mt={2}
                            p={1.5}
                            borderRadius="8px"
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={1}
                            sx={{
                                background:
                                    "#F9FAFB",

                                border:
                                    `1px solid ${COLORS.border}`,
                            }}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Showing")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        filteredTransactions.length
                                    }
                                </strong>{" "}
                                {financeText("of")}{" "}
                                <strong
                                    style={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {
                                        transactions.length
                                    }
                                </strong>{" "}
                                {financeText("transactions")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    "Treasury generated transactions are protected from manual edit/delete."
                                )}
                            </MDTypography>
                        </MDBox>

                        {/* TABLE */}

                        <MDBox
                            mt={2.5}
                            sx={{
                                "& .MuiTableContainer-root":
                                    {
                                        border:
                                            `1px solid ${COLORS.border}`,

                                        borderRadius:
                                            "10px",

                                        overflowX:
                                            "auto",

                                        boxShadow:
                                            "0 5px 18px rgba(16,24,40,.04)",
                                    },

                                "& table":
                                    {
                                        minWidth:
                                            1900,
                                    },

                                "& table thead":
                                    {
                                        background:
                                            "#F8FAFC",
                                    },

                                "& table thead th":
                                    {
                                        color:
                                            "#475467 !important",

                                        fontSize:
                                            "10px !important",

                                        fontWeight:
                                            "800 !important",

                                        letterSpacing:
                                            ".08em",

                                        paddingTop:
                                            "15px !important",

                                        paddingBottom:
                                            "15px !important",
                                    },

                                "& table tbody td":
                                    {
                                        paddingTop:
                                            "13px !important",

                                        paddingBottom:
                                            "13px !important",

                                        verticalAlign:
                                            "middle",
                                    },

                                "& table tbody tr:hover":
                                    {
                                        background:
                                            "#F5F8FF !important",

                                        boxShadow:
                                            "inset 3px 0 0 #175CD3",
                                    },
                            }}
                        >
                            {loading ? (
                                <MDBox
                                    py={8}
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <CircularProgress />
                                </MDBox>
                            ) : (
                                <DataTable
                                    table={
                                        tableData
                                    }
                                    canSearch={
                                        false
                                    }
                                    entriesPerPage={{
                                        defaultValue:
                                            10,

                                        entries: [
                                            5,
                                            10,
                                            25,
                                            50,
                                            100,
                                        ],
                                    }}
                                    showTotalEntries
                                    isSorted
                                    noEndBorder
                                />
                            )}
                        </MDBox>
                    </MDBox>
                </Card>

                {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                <FinanceDetailsSection
                    storageKey="companyCash"
                    title={financeText("Detailed analysis")}
                    subtitle={financeText("Charts and breakdowns for the current filters")}
                >
                    <Grid
                    container
                    spacing={2.5}
                    mt={0.1}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("TRY Balance")}
                            value={formatMoney(
                                dashboard
                                    .currencies
                                    .TRY,

                                "TRY"
                            )}
                            subtitle={financeText("Total Turkish Lira liquidity")}
                            icon={
                                <Payments />
                            }
                            tone="amber"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("USD Balance")}
                            value={formatMoney(
                                dashboard
                                    .currencies
                                    .USD,

                                "USD"
                            )}
                            subtitle={financeText("Total US Dollar liquidity")}
                            icon={
                                <Payments />
                            }
                            tone="green"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("EUR Balance")}
                            value={formatMoney(
                                dashboard
                                    .currencies
                                    .EUR,

                                "EUR"
                            )}
                            subtitle={financeText("Total Euro liquidity")}
                            icon={
                                <Euro />
                            }
                            tone="blue"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("GBP Balance")}
                            value={formatMoney(
                                dashboard
                                    .currencies
                                    .GBP,

                                "GBP"
                            )}
                            subtitle={financeText("Total British Pound liquidity")}
                            icon={
                                <Payments />
                            }
                            tone="purple"
                        />
                    </Grid>
                </Grid>
                </FinanceDetailsSection>

            </MDBox>

            {/* =====================================================
                NEW / EDIT TRANSACTION
            ===================================================== */}

            <Dialog
                open={
                    transactionModal
                }
                onClose={
                    closeTransactionModal
                }
                fullWidth
                maxWidth={
                    transactionForm.type ===
                    "virman"
                        ? "md"
                        : "sm"
                }
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    editingTransaction
                                        ? COLORS.purple
                                        : transactionForm.type ===
                                        "virman"
                                            ? COLORS.blue
                                            : COLORS.green,

                                background:
                                    editingTransaction
                                        ? COLORS.purpleLight
                                        : transactionForm.type ===
                                        "virman"
                                            ? COLORS.blueLight
                                            : COLORS.greenLight,
                            }}
                        >
                            {editingTransaction ? (
                                <EditOutlined />
                            ) : transactionForm.type ===
                            "virman" ? (
                                <SwapHorizOutlined />
                            ) : (
                                <Add />
                            )}
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText(
                                    editingTransaction
                                        ? "Edit Cash Transaction"
                                        : transactionForm.type ===
                                        "virman"
                                            ? "New Virman"
                                            : "New Cash Transaction"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    editingTransaction
                                        ? "Update a manual Company Cash transaction."
                                        : transactionForm.type ===
                                        "virman"
                                            ? "Transfer money between company-owned accounts."
                                            : "Record a direct bank or physical cash movement."
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Stack
                        spacing={2}
                        mt={1}
                    >
                        {/* TYPE */}

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={financeText("Transaction Type")}
                            value={
                                transactionForm.type
                            }
                            onChange={(
                                event
                            ) =>
                                handleTransactionTypeChange(
                                    event.target.value
                                )
                            }
                        >
                            <MenuItem value="cash_in">
                                {financeText("Cash In")}
                            </MenuItem>

                            <MenuItem value="cash_out">
                                {financeText("Cash Out")}
                            </MenuItem>

                            {!editingTransaction && (
                                <MenuItem value="virman">
                                    {financeText("Virman - Internal Transfer")}
                                </MenuItem>
                            )}
                        </TextField>

                        {/* =================================================
                            CASH IN / CASH OUT
                        ================================================= */}

                        {transactionForm.type !==
                            "virman" && (
                                <>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label={financeText("Account")}
                                        value={
                                            transactionForm.accountId
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleSourceAccountChange(
                                                event.target.value
                                            )
                                        }
                                    >
                                        {accounts
                                            .filter(
                                                (
                                                    account
                                                ) =>
                                                    account.isActive !==
                                                    false ||
                                                    getId(
                                                        account
                                                    ) ===
                                                    String(
                                                        transactionForm.accountId
                                                    )
                                            )
                                            .map(
                                                (
                                                    account
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            getId(
                                                                account
                                                            )
                                                        }
                                                        value={
                                                            getId(
                                                                account
                                                            )
                                                        }
                                                    >
                                                        {
                                                            account.name
                                                        }

                                                        {" · "}

                                                        {financeText(
                                                            account.type ===
                                                            "cash"
                                                                ? "Cash"
                                                                : "Bank"
                                                        )}

                                                        {" · "}

                                                        {
                                                            account.currency
                                                        }

                                                        {" · "}

                                                        {formatMoney(
                                                            account.currentBalance,

                                                            account.currency
                                                        )}
                                                    </MenuItem>
                                                )
                                            )}
                                    </TextField>

                                    {selectedTransactionAccount && (
                                        <Alert
                                            severity="info"
                                            sx={{
                                                borderRadius:
                                                    "8px",
                                            }}
                                        >
                                            {financeText("Payment Method")}:{" "}
                                            <strong>
                                                {financeText(
                                                    selectedTransactionAccount.type ===
                                                    "cash"
                                                        ? "Cash"
                                                        : "Bank"
                                                )}
                                            </strong>

                                            {" · "}

                                            {financeText("Current Balance")}:{" "}
                                            <strong>
                                                {formatMoney(
                                                    selectedTransactionAccount.currentBalance,

                                                    selectedTransactionAccount.currency
                                                )}
                                            </strong>
                                        </Alert>
                                    )}

                                    <MDBox
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                            },
                                            gap: 2,
                                        }}
                                    >
                                        <MDBox>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label={financeText("Amount")}
                                                value={
                                                    transactionForm.amount
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setTransactionForm(
                                                        (
                                                            previous
                                                        ) => ({
                                                            ...previous,

                                                            amount:
                                                            event.target.value,
                                                        })
                                                    )
                                                }
                                                InputProps={{
                                                    startAdornment:
                                                        selectedTransactionAccount ? (
                                                            <InputAdornment position="start">
                                                                {
                                                                    selectedTransactionAccount.currency
                                                                }
                                                            </InputAdornment>
                                                        ) : null,
                                                }}
                                            />
                                        </MDBox>

                                        <MDBox>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={financeText("Category")}
                                                value={
                                                    transactionForm.category
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setTransactionForm(
                                                        (
                                                            previous
                                                        ) => ({
                                                            ...previous,

                                                            category:
                                                            event.target.value,
                                                        })
                                                    )
                                                }
                                            />
                                        </MDBox>
                                    </MDBox>

                                    {selectedTransactionAccount &&
                                        Number(
                                            transactionForm.amount
                                        ) >
                                        0 && (
                                            <Alert
                                                severity={
                                                    transactionForm.type ===
                                                    "cash_in"
                                                        ? "success"
                                                        : Number(
                                                            transactionForm.amount
                                                        ) >
                                                        transactionAvailableBalance
                                                            ? "error"
                                                            : "warning"
                                                }
                                                sx={{
                                                    borderRadius:
                                                        "8px",
                                                }}
                                            >
                                                {financeText("Current Balance")}:{" "}
                                                <strong>
                                                    {formatMoney(
                                                        selectedTransactionAccount.currentBalance,

                                                        selectedTransactionAccount.currency
                                                    )}
                                                </strong>

                                                {" · "}

                                                {financeText("Balance After Transaction")}:{" "}
                                                <strong>
                                                    {formatMoney(
                                                        transactionForm.type ===
                                                        "cash_in"
                                                            ? transactionAvailableBalance +
                                                            Number(
                                                                transactionForm.amount
                                                            )
                                                            : transactionAvailableBalance -
                                                            Number(
                                                                transactionForm.amount
                                                            ),

                                                        selectedTransactionAccount.currency
                                                    )}
                                                </strong>
                                            </Alert>
                                        )}
                                </>
                            )}

                        {/* =================================================
                            VIRMAN
                        ================================================= */}

                        {transactionForm.type ===
                            "virman" && (
                                <>
                                    <Alert
                                        severity="info"
                                        sx={{
                                            borderRadius:
                                                "8px",
                                        }}
                                    >
                                        {financeText(
                                            "Virman is an internal transfer between company accounts. It does not create company-wide Cash In or Cash Out."
                                        )}
                                    </Alert>

                                    <MDBox
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                md: "1fr auto 1fr",
                                            },
                                            gap: 2,
                                            alignItems: "start",
                                        }}
                                    >
                                        <MDBox>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label={financeText("From Account")}
                                                value={
                                                    transactionForm.accountId
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    handleSourceAccountChange(
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                {accounts
                                                    .filter(
                                                        (
                                                            account
                                                        ) =>
                                                            account.isActive !==
                                                            false
                                                    )
                                                    .map(
                                                        (
                                                            account
                                                        ) => (
                                                            <MenuItem
                                                                key={
                                                                    getId(
                                                                        account
                                                                    )
                                                                }
                                                                value={
                                                                    getId(
                                                                        account
                                                                    )
                                                                }
                                                            >
                                                                {
                                                                    account.name
                                                                }

                                                                {" · "}

                                                                {
                                                                    account.currency
                                                                }

                                                                {" · "}

                                                                {formatMoney(
                                                                    account.currentBalance,

                                                                    account.currency
                                                                )}
                                                            </MenuItem>
                                                        )
                                                    )}
                                            </TextField>
                                        </MDBox>

                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                height: {
                                                    md: 40,
                                                },
                                            }}
                                        >
                                            <SwapHorizOutlined
                                                sx={{
                                                    color:
                                                    COLORS.blue,

                                                    fontSize:
                                                        30,
                                                }}
                                            />
                                        </MDBox>

                                        <MDBox>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label={financeText("To Account")}
                                                disabled={
                                                    !selectedTransactionAccount
                                                }
                                                value={
                                                    transactionForm.destinationAccountId
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setTransactionForm(
                                                        (
                                                            previous
                                                        ) => ({
                                                            ...previous,

                                                            destinationAccountId:
                                                            event.target.value,
                                                        })
                                                    )
                                                }
                                                helperText={
                                                    selectedTransactionAccount
                                                        ? `${financeText("Only")} ${selectedTransactionAccount.currency} ${financeText("accounts are shown.")}`
                                                        : financeText("Select source account first.")
                                                }
                                            >
                                                {virmanDestinationAccounts.map(
                                                    (
                                                        account
                                                    ) => (
                                                        <MenuItem
                                                            key={
                                                                getId(
                                                                    account
                                                                )
                                                            }
                                                            value={
                                                                getId(
                                                                    account
                                                                )
                                                            }
                                                        >
                                                            {
                                                                account.name
                                                            }

                                                            {" · "}

                                                            {formatMoney(
                                                                account.currentBalance,

                                                                account.currency
                                                            )}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                        </MDBox>
                                    </MDBox>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label={financeText("Virman Amount")}
                                        value={
                                            transactionForm.amount
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setTransactionForm(
                                                (
                                                    previous
                                                ) => ({
                                                    ...previous,

                                                    amount:
                                                    event.target.value,
                                                })
                                            )
                                        }
                                        InputProps={{
                                            startAdornment:
                                                selectedTransactionAccount ? (
                                                    <InputAdornment position="start">
                                                        {
                                                            selectedTransactionAccount.currency
                                                        }
                                                    </InputAdornment>
                                                ) : null,
                                        }}
                                    />

                                    {selectedTransactionAccount &&
                                        selectedDestinationAccount &&
                                        Number(
                                            transactionForm.amount
                                        ) >
                                        0 && (
                                            <MDBox
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                        xs: "1fr",
                                                        md: "1fr 1fr",
                                                    },
                                                    gap: 2,
                                                }}
                                            >
                                                <MDBox>
                                                    <MDBox
                                                        p={2}
                                                        borderRadius="8px"
                                                        sx={{
                                                            background:
                                                            COLORS.redLight,

                                                            border:
                                                                "1px solid #FECDCA",
                                                        }}
                                                    >
                                                        <MDTypography
                                                            variant="caption"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color:
                                                                COLORS.red,
                                                            }}
                                                        >
                                                            {financeText("FROM")}
                                                        </MDTypography>

                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            display="block"
                                                            mt={0.5}
                                                        >
                                                            {
                                                                selectedTransactionAccount.name
                                                            }
                                                        </MDTypography>

                                                        <MDTypography
                                                            variant="caption"
                                                            display="block"
                                                            sx={{
                                                                color:
                                                                COLORS.subtext,
                                                            }}
                                                        >
                                                            {formatMoney(
                                                                selectedTransactionAccount.currentBalance,

                                                                selectedTransactionAccount.currency
                                                            )}

                                                            {" → "}

                                                            {formatMoney(
                                                                Number(
                                                                    selectedTransactionAccount.currentBalance
                                                                ) -
                                                                Number(
                                                                    transactionForm.amount
                                                                ),

                                                                selectedTransactionAccount.currency
                                                            )}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>

                                                <MDBox>
                                                    <MDBox
                                                        p={2}
                                                        borderRadius="8px"
                                                        sx={{
                                                            background:
                                                            COLORS.greenLight,

                                                            border:
                                                                "1px solid #ABEFC6",
                                                        }}
                                                    >
                                                        <MDTypography
                                                            variant="caption"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color:
                                                                COLORS.green,
                                                            }}
                                                        >
                                                            {financeText("TO")}
                                                        </MDTypography>

                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            display="block"
                                                            mt={0.5}
                                                        >
                                                            {
                                                                selectedDestinationAccount.name
                                                            }
                                                        </MDTypography>

                                                        <MDTypography
                                                            variant="caption"
                                                            display="block"
                                                            sx={{
                                                                color:
                                                                COLORS.subtext,
                                                            }}
                                                        >
                                                            {formatMoney(
                                                                selectedDestinationAccount.currentBalance,

                                                                selectedDestinationAccount.currency
                                                            )}

                                                            {" → "}

                                                            {formatMoney(
                                                                Number(
                                                                    selectedDestinationAccount.currentBalance
                                                                ) +
                                                                Number(
                                                                    transactionForm.amount
                                                                ),

                                                                selectedDestinationAccount.currency
                                                            )}
                                                        </MDTypography>
                                                    </MDBox>
                                                </MDBox>
                                            </MDBox>
                                        )}

                                    {selectedTransactionAccount &&
                                        Number(
                                            transactionForm.amount
                                        ) >
                                        Number(
                                            selectedTransactionAccount.currentBalance
                                        ) && (
                                            <Alert
                                                severity="error"
                                                sx={{
                                                    borderRadius:
                                                        "8px",
                                                }}
                                            >
                                                {financeText(financeText("Source account has insufficient balance."))}
                                            </Alert>
                                        )}
                                </>
                            )}

                        {/* COMMON */}

                        <TextField
                            fullWidth
                            size="small"
                            label={financeText("Reference No")}
                            value={
                                transactionForm.referenceNo
                            }
                            onChange={(
                                event
                            ) =>
                                setTransactionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        referenceNo:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={financeText("Description")}
                            value={
                                transactionForm.description
                            }
                            onChange={(
                                event
                            ) =>
                                setTransactionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        description:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        {transactionForm.type !==
                            "virman" && (
                                <Alert
                                    severity="info"
                                    sx={{
                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    {financeText(
                                        "Credit Card, POS, Cheque and Promissory Note transactions are generated from their own Treasury modules. Manual Company Cash transactions are limited to Bank and Physical Cash."
                                    )}
                                </Alert>
                            )}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            transactionSaving
                        }
                        onClick={
                            closeTransactionModal
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            transactionSaving
                        }
                        onClick={
                            saveTransaction
                        }
                        sx={{
                            background:
                                transactionForm.type ===
                                "virman"
                                    ? `${COLORS.blue} !important`
                                    : `${COLORS.navy} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {transactionSaving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText(
                            editingTransaction
                                ? "Update Transaction"
                                : transactionForm.type ===
                                "virman"
                                    ? "Complete Virman"
                                    : "Save Transaction"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                ADD PHYSICAL CASH
            ===================================================== */}

            <Dialog
                open={
                    cashAccountModal
                }
                onClose={
                    closeCashAccountModal
                }
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={44}
                            height={44}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                COLORS.green,

                                background:
                                COLORS.greenLight,

                                border:
                                    "1px solid #ABEFC6",
                            }}
                        >
                            <LocalAtmOutlined />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {financeText("Add Cash Register")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Create a physical company cash account.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Stack
                        spacing={2}
                        mt={1}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label={financeText("Cash Register Name")}
                            placeholder={financeText("Head Office TRY Cash")}
                            value={
                                cashAccountForm.name
                            }
                            onChange={(
                                event
                            ) =>
                                setCashAccountForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        name:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={financeText("Currency")}
                            value={
                                cashAccountForm.currency
                            }
                            onChange={(
                                event
                            ) =>
                                setCashAccountForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        currency:
                                        event.target.value,
                                    })
                                )
                            }
                        >
                            {CURRENCIES.map(
                                (
                                    item
                                ) => (
                                    <MenuItem
                                        key={
                                            item
                                        }
                                        value={
                                            item
                                        }
                                    >
                                        {item}
                                    </MenuItem>
                                )
                            )}
                        </TextField>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={financeText("Opening Balance")}
                            value={
                                cashAccountForm.openingBalance
                            }
                            onChange={(
                                event
                            ) =>
                                setCashAccountForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        openingBalance:
                                        event.target.value,
                                    })
                                )
                            }
                            InputProps={{
                                startAdornment:
                                    (
                                        <InputAdornment position="start">
                                            {
                                                cashAccountForm.currency
                                            }
                                        </InputAdornment>
                                    ),
                            }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={financeText("Description")}
                            placeholder={financeText("Head office physical cash register...")}
                            value={
                                cashAccountForm.description
                            }
                            onChange={(
                                event
                            ) =>
                                setCashAccountForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        description:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        <Alert
                            severity="info"
                            sx={{
                                borderRadius:
                                    "8px",
                            }}
                        >
                            {financeText(
                                "Bank accounts are created and connected from Bank Accounts. This form should only be used for physical company cash registers."
                            )}
                        </Alert>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            cashAccountSaving
                        }
                        onClick={
                            closeCashAccountModal
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            cashAccountSaving
                        }
                        onClick={
                            saveCashAccount
                        }
                        sx={{
                            background:
                                `${COLORS.green} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {cashAccountSaving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Add Cash Register")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                DELETE TRANSACTION
            ===================================================== */}

            <Dialog
                open={Boolean(
                    deleteTransactionTarget
                )}
                onClose={() => {
                    if (
                        !transactionSaving
                    ) {
                        setDeleteTransactionTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        textAlign:
                            "center",

                        pt:
                            4,
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.redLight,
                        }}
                    >
                        <WarningAmberRounded
                            sx={{
                                color:
                                COLORS.red,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color:
                            COLORS.text,
                        }}
                    >
                        {financeText("Delete Transaction?")}
                    </MDTypography>

                    <MDTypography
                        variant="body2"
                        mt={1}
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        <strong>
                            {
                                deleteTransactionTarget
                                    ?.transactionNo
                            }
                        </strong>{" "}
                        {financeText(
                            "will be permanently deleted. Its effect on the account balance will be reversed automatically."
                        )}
                    </MDTypography>

                    {deleteTransactionTarget && (
                        <Chip
                            label={formatMoney(
                                deleteTransactionTarget.amount,

                                deleteTransactionTarget.currency
                            )}
                            sx={{
                                mt:
                                    2,

                                color:
                                    deleteTransactionTarget.type ===
                                    "cash_in"
                                        ? COLORS.green
                                        : COLORS.red,

                                background:
                                    deleteTransactionTarget.type ===
                                    "cash_in"
                                        ? COLORS.greenLight
                                        : COLORS.redLight,

                                fontWeight:
                                    700,
                            }}
                        />
                    )}

                    <Alert
                        severity="warning"
                        sx={{
                            mt:
                                2,

                            textAlign:
                                "left",

                            borderRadius:
                                "8px",
                        }}
                    >
                        {financeText(
                            "Only direct Bank or Cash transactions can be deleted here. POS, Cheque, Credit Card, Promissory Note and Virman transactions must remain linked to their Treasury source."
                        )}
                    </Alert>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            transactionSaving
                        }
                        onClick={() =>
                            setDeleteTransactionTarget(
                                null
                            )
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            transactionSaving
                        }
                        onClick={
                            confirmDeleteTransaction
                        }
                        sx={{
                            background:
                                `${COLORS.red} !important`,

                            color:
                                "#FFFFFF !important",

                            borderRadius:
                                "10px",

                            boxShadow:
                                "none",
                        }}
                    >
                        {transactionSaving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Delete Transaction")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                SNACKBAR
            ===================================================== */}

            <Snackbar
                open={
                    notification.open
                }
                autoHideDuration={
                    4500
                }
                onClose={
                    closeNotification
                }
                anchorOrigin={{
                    vertical:
                        "bottom",

                    horizontal:
                        "right",
                }}
            >
                <Alert
                    severity={
                        notification.severity
                    }
                    variant="filled"
                    onClose={
                        closeNotification
                    }
                    sx={{
                        width:
                            "100%",

                        borderRadius:
                            "8px",
                    }}
                >
                    {
                        notification.message
                    }
                </Alert>
            </Snackbar>

            <Footer />
        </DashboardLayout>
    );
};

export default CompanyCash;